import { addNotification } from "./notification.service";

const SESSIONS_KEY = "contactus:sessions";
const MSG_KEY_PREFIX = "contactus:messages:";

function getCurrentUserId() {
  try {
    const raw = localStorage.getItem("user");
    const user = raw ? JSON.parse(raw) : null;
    return user?.userId ? String(user.userId) : "";
  } catch {
    return "";
  }
}

function getScopedSessionsKey() {
  const userId = getCurrentUserId();
  return userId ? `${SESSIONS_KEY}:${userId}` : SESSIONS_KEY;
}

function getScopedMessagesKey(sessionId) {
  const userId = getCurrentUserId();
  return userId ? `${MSG_KEY_PREFIX}${userId}:${sessionId}` : `${MSG_KEY_PREFIX}${sessionId}`;
}

function safeRead(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function safeWrite(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage quota / blocked storage: ignore to avoid breaking UI flows
  }
}

export function getContactSessions() {
  return safeRead(getScopedSessionsKey(), []);
}

export function getSessionMessages(sessionId) {
  if (!sessionId) return [];
  return safeRead(getScopedMessagesKey(sessionId), []);
}

function upsertSession(sessionId, patch) {
  const sessions = getContactSessions();
  const idx = sessions.findIndex(s => s.sessionId === sessionId);
  const next = idx >= 0
    ? sessions.map((s, i) => (i === idx ? { ...s, ...patch } : s))
    : [{ sessionId, unreadCount: 0, ...patch }, ...sessions];

  safeWrite(getScopedSessionsKey(), next);
  return next;
}

function writeSessionMessages(sessionId, list) {
  safeWrite(getScopedMessagesKey(sessionId), list);
}

function extractSessionIdFromTopic(topic) {
  const parts = String(topic || "").split("/");
  // ptduo/{app}/user/fe/{userId}/ContactUs/{sessionId}
  return parts[6] || "";
}

export function applyContactUsMessage(payload = {}, topic = "") {
  const sessionId = payload.sessionId || extractSessionIdFromTopic(topic);
  if (!sessionId) return;

  const messageId = payload.messageId;
  if (!messageId) return;

  const list = getSessionMessages(sessionId);
  const idx = list.findIndex(m => m.messageId === messageId);

  // Trainer paneli için: sadece MEMBER'dan gelen yeni mesajlar "okunmamış" sayılmalı.
  // MQTT dokümanında senderApp: 1=Trainer, 2=Member.
  const senderApp = payload.senderApp ?? payload.senderType; // senderType fallback (bazı payload'larda olabilir)
  const isIncomingForTrainer =
    String(senderApp) === "2" || Number(senderApp) === 2;

  // DELETE
  if (payload.isDeleted === true) {
    const next = idx >= 0 ? list.filter(m => m.messageId !== messageId) : list;
    writeSessionMessages(sessionId, next);
    window.dispatchEvent(new CustomEvent("ptduo:contactus", { detail: { type: "delete", sessionId, payload } }));
    return;
  }

  // UPDATE or ADD
  let nextList;
  if (idx >= 0) {
    nextList = list.map((m, i) => (i === idx ? { ...m, ...payload } : m));
    writeSessionMessages(sessionId, nextList);
    window.dispatchEvent(new CustomEvent("ptduo:contactus", { detail: { type: "update", sessionId, payload } }));
  } else {
    nextList = [...list, payload];
    writeSessionMessages(sessionId, nextList);
    window.dispatchEvent(new CustomEvent("ptduo:contactus", { detail: { type: "add", sessionId, payload } }));

    // UI için ayrıca bildirim üret (nav-bar badge/listesinde görünsün)
    const message = payload.message || "Yeni mesajınız var.";
    if (isIncomingForTrainer) {
      const title = payload.senderName ? `Yeni Mesaj • ${payload.senderName}` : "Yeni Mesaj";
      addNotification({
        id: `contactus-${sessionId}-${messageId}`,
        type: "ContactUs",
        title,
        message,
        time: payload.createdTime ? new Date(payload.createdTime).toLocaleString("tr-TR") : new Date().toLocaleString("tr-TR"),
        isRead: false,
        topic,
        payload: { sessionId, messageId }
      });
    }

    // session summary
    const existing = getContactSessions().find(s => s.sessionId === sessionId) || {};
    const prevUnread = Number(existing?.unreadCount || 0);
    upsertSession(sessionId, {
      lastMessage: message,
      lastTime: payload.createdTime || new Date().toISOString(),
      // incoming → unread artar ve trainer okumamış kabul edilir
      unreadCount: isIncomingForTrainer ? (prevUnread + 1) : prevUnread,
      isReadTrainer: isIncomingForTrainer ? false : true,
      // trainer mesaj attıysa member tarafında okunmadı kabul edilebilir; member mesaj attıysa member zaten okumuş kabul edilir
      isReadMember: isIncomingForTrainer ? true : false
    });
  }
}

export function syncContactSessionsFromApi(list = []) {
  const rows = Array.isArray(list) ? list : [];
  rows.forEach((s) => {
    const sessionId = s?.sessionId ? String(s.sessionId) : "";
    if (!sessionId) return;
    upsertSession(sessionId, {
      studioId: s?.studioId || null,
      categoryId: s?.categoryId || null,
      memberId: s?.memberId || null,
      memberName: s?.memberName || "",
      subjectId: s?.subjectId || null,
      subjectName: s?.subjectName || "",
      trainerId: s?.trainerId || null,
      trainerName: s?.trainerName || "",
      groupId: s?.groupId || null,
      groupName: s?.groupName || "",
      isReadTrainer: s?.isReadTrainer,
      isReadMember: s?.isReadMember,
      lastMessageAt: s?.lastMessageAt || null,
      closedAt: s?.closedAt || null,
      createdTime: s?.createdTime || null,
      modifiedTime: s?.modifiedTime || null,
    });
  });
}

export function markSessionRead(sessionId) {
  if (!sessionId) return;
  upsertSession(sessionId, { unreadCount: 0, isReadTrainer: true });
  // Liste ekranı (contact-sessions) realtime event ile kendini yeniliyor.
  window.dispatchEvent(new CustomEvent("ptduo:contactus", { detail: { type: "read", sessionId } }));
}

export function markSessionClosed(sessionId, closedAt = new Date().toISOString()) {
  if (!sessionId) return;
  upsertSession(sessionId, {
    closedAt,
    unreadCount: 0,
    isReadTrainer: true,
    isReadMember: true,
  });
  window.dispatchEvent(new CustomEvent("ptduo:contactus", { detail: { type: "closed", sessionId, closedAt } }));
}

